import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { BehaviorSubject, combineLatest, delay, distinct, map } from 'rxjs';
import {
    BaselineDeviation,
    BaselinePostureCountsByDate,
    CurrentPostureCount,
    GroupBaselineDeviation,
    Header,
    TicketCount,
    TopAlert,
    TopUser,
} from '../interfaces/ng-oct-report.interface';
import { CategoryService } from '../services/category.service';
import { NgOctReportService } from '../services/ng-oct-report.service';
import { groupBy } from '../utils';

interface ItenantBaselineDeviationsByCategory {
    [category: string]: {
        [name: string]: BaselineDeviation | null;
    };
}
@Component({
    selector: 'ng-oct-report',
    templateUrl: './ng-oct-report.component.html',
    styleUrls: ['./ng-oct-report.component.scss'],
})
export class NgOctReportComponent implements OnInit {
    @Output() rendered$ = new EventEmitter<boolean>();
    header$ = new BehaviorSubject<Header | null>(null);
    allBaselinesPostureCount$ = new BehaviorSubject<{
        tenant_count: CurrentPostureCount;
        group_count: CurrentPostureCount;
    } | null>(null);
    alerts$ = new BehaviorSubject<TopAlert[] | null>(null);
    ticketCount$ = new BehaviorSubject<TicketCount[] | null>(null);
    alertsByUsers$ = new BehaviorSubject<TopUser[] | null>(null);
    tenant_baselines_posture_controls_in_this_period$ = new BehaviorSubject<BaselinePostureCountsByDate | null>(null);
    group_baselines_posture_controls_in_this_period$ = new BehaviorSubject<BaselinePostureCountsByDate | null>(null);
    tenantBaselineDeviations$ = new BehaviorSubject<ItenantBaselineDeviationsByCategory | null>(null);
    groupBaselineDeviations$ = new BehaviorSubject<GroupBaselineDeviation | null>(null);

    loaded$: { [key: string]: BehaviorSubject<any> } = {
        header: new BehaviorSubject(false),
        categories: new BehaviorSubject(false),
        all_baselines_current_posture_count: new BehaviorSubject(false),
        tenant_baselines_posture_controls_in_this_period: new BehaviorSubject(false),
        group_baselines_posture_controls_in_this_period: new BehaviorSubject(false),
        top_alerts: new BehaviorSubject(false),
        ticket_count: new BehaviorSubject(false),
        tenant_baseline_deviations: new BehaviorSubject(false),
        group_baseline_deviations: new BehaviorSubject(false),
    };

    public all_loaded$ = combineLatest([
        this.loaded$['header'],
        this.loaded$['categories'],
        this.loaded$['tenant_baselines_posture_controls_in_this_period'],
        this.loaded$['group_baselines_posture_controls_in_this_period'],
        this.loaded$['all_baselines_current_posture_count'],
        this.loaded$['top_alerts'],
        this.loaded$['ticket_count'],
        this.loaded$['tenant_baseline_deviations'],
        this.loaded$['group_baseline_deviations'],
    ]).pipe(
        delay(2000),
        map((all_loaded) => all_loaded.every((loaded) => loaded)),
    );

    tenant_catagories: string[] = [];
    group_categories: string[] = [];

    baselines_by_tc: { [tenant_category: string]: GroupBaselineDeviation } = {};
    baselines_by_gc: { [group_category: string]: GroupBaselineDeviation } = {};

    hasPSA: boolean = false;

    constructor(
        private reportService: NgOctReportService,
        private categoryService: CategoryService,
    ) {}

    ngOnInit(): void {
        this.loadHeader();
        this.loadCategories();
        this.loadAllBaselinePostureCount();
        this.loadTopAlerts();
        this.loadTicketCount();
        this.loadBaselinesPostureControlsInThisPeriod();
        this.loadTenantBaselineDeviations();
        this.loadGroupBaselineDeviations();
        this.all_loaded$.subscribe((all_loaded) => this.rendered$.emit(all_loaded));
    }

    loadHeader() {
        this.reportService.header$.pipe(distinct()).subscribe((_header) => {
            this.header$.next(_header);
            this.loaded$['header'].next(true);
        });
    }

    loadCategories() {
        this.tenant_catagories = this.categoryService.tenant_categories;
        this.loaded$['categories'].next(true);
    }

    loadTopAlerts() {
        this.reportService.topAlerts$.pipe(distinct()).subscribe((alerts) => {
            let alertsByUsers: any[] | null = [];
            if (alerts) {
                alerts = alerts
                    .filter(
                        (a) =>
                            a.source_type === 'microsoft-event' &&
                            (a.severity === 'critical' || a.severity === 'danger'),
                    )
                    .map((a) => ({ ...a, timestamp: new Date(a.timestamp).toString() }));

                const groupedObjByUser = groupBy(
                    alerts.filter((a) => !!a.actor),
                    'actor',
                );
                alertsByUsers = Object.keys(groupedObjByUser)
                    .map((k) => ({
                        actor: k,
                        alerts: groupedObjByUser[k],
                        counts: {
                            critical: groupedObjByUser[k].filter((a: TopAlert) => a.severity === 'critical').length,
                            danger: groupedObjByUser[k].filter((a: TopAlert) => a.severity === 'danger').length,
                            warning: groupedObjByUser[k].filter((a: TopAlert) => a.severity === 'warning').length,
                        },
                    }))
                    .sort((a, b) =>
                        a.counts.critical > b.counts.critical
                            ? -1
                            : a.counts.critical === b.counts.critical
                              ? a.counts.danger > b.counts.danger
                                  ? -1
                                  : a.counts.danger === b.counts.danger
                                    ? a.counts.warning > b.counts.warning
                                        ? -1
                                        : a.counts.warning === b.counts.warning
                                          ? -1
                                          : 1
                                    : 1
                              : 1,
                    )
                    .splice(0, 5);
            }
            this.alerts$.next(alerts);
            this.alertsByUsers$.next(alertsByUsers);
            this.loaded$['top_alerts'].next(true);
        });
    }

    loadTicketCount() {
        this.reportService.ticketCount$.pipe(distinct()).subscribe((ticket_counts) => {
            console.log('ticket_counts', ticket_counts);
            if (!!ticket_counts && ticket_counts!.length > 0) {
                this.ticketCount$.next(ticket_counts);
                this.hasPSA = true;
            } else {
                this.hasPSA = false;
            }
            this.loaded$['ticket_count'].next(true);
            console.log('hasPSA', this.hasPSA);
        });
    }

    loadAllBaselinePostureCount() {
        this.reportService.allBaselines_posture_count$.pipe(distinct()).subscribe((counts) => {
            this.allBaselinesPostureCount$.next(counts);
            this.loaded$['all_baselines_current_posture_count'].next(true);
        });
    }

    loadBaselinesPostureControlsInThisPeriod() {
        this.reportService.tenant_baselines_posture_controls_in_this_period$
            .pipe(distinct())
            .subscribe((posture_controls_count) => {
                this.tenant_baselines_posture_controls_in_this_period$.next(posture_controls_count);
                this.loaded$['tenant_baselines_posture_controls_in_this_period'].next(true);
            });
        this.reportService.group_baselines_posture_controls_in_this_period$
            .pipe(distinct())
            .subscribe((posture_controls_count) => {
                this.group_baselines_posture_controls_in_this_period$.next(posture_controls_count);
                this.loaded$['group_baselines_posture_controls_in_this_period'].next(true);
            });
    }

    loadTenantBaselineDeviations() {
        combineLatest([this.header$, this.reportService.tenantBaselineDeviationsByBaseline$])
            .pipe(distinct())
            .subscribe(([_header, tenantBaselineDeviationsByBaseline]) => {
                console.log('Tenant Baseline Deviations Per Baseline');
                console.log(JSON.stringify(tenantBaselineDeviationsByBaseline));
                const tenantBaselineDeviationsByCategory = tenantBaselineDeviationsByBaseline
                    ? this.formatTenantBaselineDeviationsByCategory(tenantBaselineDeviationsByBaseline)
                    : {};
                this.tenantBaselineDeviations$.next(tenantBaselineDeviationsByCategory);

                this.loaded$['tenant_baseline_deviations'].next(true);
            });
    }

    loadGroupBaselineDeviations() {
        combineLatest([this.header$, this.reportService.groupBaselineDeviationsByBaseline$])
            .pipe(distinct())
            .subscribe(([header, groupBaselineDeviationsByBaseline]) => {
                console.log('Group Baseline Deviations Per Baseline');
                console.log(JSON.stringify(groupBaselineDeviationsByBaseline));
                const groupUserBaselineDeviationsByBaseline = groupBaselineDeviationsByBaseline
                    ? this.formatGroupUserBaselineDeviationsByCategory(groupBaselineDeviationsByBaseline)
                    : {};
                this.groupBaselineDeviations$.next(groupUserBaselineDeviationsByBaseline);
                this.loaded$['group_baseline_deviations'].next(true);
            });
    }

    private formatTenantBaselineDeviationsByCategory(
        tenantBaselineDeviations: BaselineDeviation[],
    ): ItenantBaselineDeviationsByCategory {
        const tmp: ItenantBaselineDeviationsByCategory = {};

        const tenantBaselineDeviationsByCategory = this.categoryService.tenantBaselineNamesByCategory;
        tenantBaselineDeviationsByCategory.forEach((v, category, map) => {
            tmp[category] = {};
            for (const name of map.get(category)!) {
                const tenantBaselineDeviationsByName = tenantBaselineDeviations!
                    .filter((tbd) => tbd.category === category)
                    .find((tbd) => tbd.name === name);
                tmp[category][name] = tenantBaselineDeviationsByName ?? null;
            }
        });
        console.log(tmp);
        return tmp;
    }

    private formatGroupUserBaselineDeviationsByCategory(
        groupBaselineDeviations: BaselineDeviation[],
    ): GroupBaselineDeviation {
        const byGroupBaselineUserName: GroupBaselineDeviation = {};

        const byGroupName: { [group: string]: BaselineDeviation[] } = groupBy(groupBaselineDeviations, 'group_name');

        Object.entries(byGroupName).forEach(([group_name, arr1]) => {
            const byBaselineAndUserName: {
                [baseline: string]: {
                    [user: string]: BaselineDeviation;
                } | null;
            } = {};

            const byBaselineObj: { [baseline: string]: BaselineDeviation[] } = groupBy(arr1, 'name');

            Object.entries(byBaselineObj).forEach(([baseline, arr2]) => {
                const byUserNameObj: { [user: string]: BaselineDeviation } = groupBy(arr2, 'user_name', true);
                byBaselineAndUserName[baseline] = Object.keys(byUserNameObj)[0] === 'undefined' ? null : byUserNameObj;
            });

            byGroupBaselineUserName[group_name] = byBaselineAndUserName;
        });

        console.log('Deviations by Group');
        console.log(byGroupBaselineUserName);
        return byGroupBaselineUserName;
    }
}
